import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { loginUser, logoutUser } from '../api/authApi';
import { useAuthStore } from '../store/authStore';
import type { AccountInfo, LoginCredentials } from '../types/auth.types';

interface UseAuthOptions {
  onLoginSuccess?: (accounts: AccountInfo[]) => void;
}

export function useAuth(options?: UseAuthOptions) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, setTokens, setActiveAccount, logout: clearAuth, user, isAuthenticated } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => loginUser(credentials),
    onSuccess: (response) => {
      setUser(response.data.user);
      setTokens(response.data.tokens);
      const accounts = response.data.accounts ?? [];
      if (options?.onLoginSuccess) {
        options.onLoginSuccess(accounts);
      } else {
        if (accounts.length) setActiveAccount(accounts[0]);
        const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/account';
        navigate(from, { replace: true });
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => logoutUser(),
    onSettled: () => {
      clearAuth();
      navigate('/login', { replace: true });
    },
  });

  return {
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
    currentUser: user,
    isAuthenticated,
  };
}
